import React, {ChangeEvent, useEffect, useState} from 'react'
import {useAccountContext} from '../layout/AccountLayout'
import {useCreateTicketMutation} from '../../services/residentPortalApi'
import {useNavigate, useSearchParams} from 'react-router-dom'
import SubmitButton from '../common/SubmitButton'
import {getNavigationPaths} from '../../services/navigation'
import Breadcrumbs from '../navigation/Breadcrumbs'
import toast from 'react-hot-toast'
import {getLogger} from '../../services/logging'

const logger = getLogger('AddSupportRequestPage')

interface SupportRequest {
  category: string
  subject: string
  description: string
  propertyId: string
  unitId: string
  priority: string
}

const categories = [
  {
    id: 'billing',
    category: 'Billing'
  },
  {
    id: 'maintenance',
    category: 'Maintenance'
  }
]

const priorities = [
  {
    id: 'High',
    priority: 'High'
  },
  {
    id: 'Medium',
    priority: 'Medium'
  },
  {
    id: 'Low',
    priority: 'Low'
  }
]

function findCategory(id: string) {
  for (let category of categories) {
    if (category.id === id) {
      return category
    }
  }
  return categories[0]
}

function findPriority(id: string) {
  for (let priority of priorities) {
    if (priority.id === id) {
      return priority
    }
  }
  return priorities[0]
}

function isValid({category, subject, description}: SupportRequest): boolean {
  return !(!category.length || !subject.length || !description.length)
}

const AddSupportRequestPage = () => {
  const {accountId, reservation} = useAccountContext()
  const propertyId = reservation.property.id
  const unitId = reservation.unit.id
  const navigate = useNavigate()
  const paths = getNavigationPaths(accountId)
  const [createTicket, createTicketResult] = useCreateTicketMutation()
  const initialState: SupportRequest = {
    category: categories[0].category,
    subject: '',
    description: '',
    propertyId,
    unitId,
    priority: priorities[1].priority
  }
  const [supportRequest, setSupportRequest] = useState(initialState)

  // prepopulate the form using query string params
  const [searchParams] = useSearchParams()
  const categoryParam = searchParams.get('category')
  const subjectParam = searchParams.get('subject')
  const priorityParam = searchParams.get('priority')

  useEffect(() => {
    if (categoryParam || subjectParam) {
      const {category} = findCategory(categoryParam)
      const {priority} = findPriority(priorityParam)
      setSupportRequest({
        category,
        subject: subjectParam,
        description: '',
        propertyId,
        unitId,
        priority
      })
    }
  }, [categoryParam, subjectParam])

  return (
    <section>
      <Breadcrumbs
        items={[
          {label: 'Account', path: paths.Account, active: false},
          {
            label: 'Resident Support',
            path: paths.SupportRequests,
            active: false
          },
          {label: 'Create Support Request', active: true}
        ]}
      />
      <div className="card-default w-full">
        <div className="card-body-default">
          <h1 className="card-header">Create Support Request</h1>
          <div className="form-control">
            <label className="label label-text">Category</label>
            <select
              className="select select-primary"
              onChange={e =>
                setSupportRequest({
                  ...supportRequest,
                  category: categories[e.target.selectedIndex].category
                })
              }
            >
              {categories.map(({id, category}) => (
                <option key={id}>{category}</option>
              ))}
            </select>
          </div>
          <label className="label label-text">Priority</label>
          <select
            className="select select-primary"
            defaultValue={priorities[1].priority}
            onChange={e =>
              setSupportRequest({
                ...supportRequest,
                priority: priorities[e.target.selectedIndex].priority
              })
            }
          >
            {priorities.map(({id, priority}) => (
              <option key={id}>{priority}</option>
            ))}
          </select>
          <div className="form-control mt-2">
            <label className="label label-text">Subject</label>
            <input
              type="text"
              className="input input-primary"
              placeholder="Short summary of the support request"
              value={supportRequest.subject}
              onChange={e =>
                setSupportRequest({...supportRequest, subject: e.target.value})
              }
            />
          </div>
          <div className="form-control mt-2">
            <label className="label label-text">Description</label>
            <textarea
              rows={6}
              className="textarea textarea-primary"
              placeholder="Detailed description of the support request"
              value={supportRequest.description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setSupportRequest({
                  ...supportRequest,
                  description: e.target.value
                })
              }
            />
          </div>
          <SubmitButton
            className="btn btn-primary w-48 mt-4"
            onSubmit={() => {
              const ticket = {
                reservationId: accountId,
                type: supportRequest.category,
                subject: supportRequest.subject,
                description: supportRequest.description,
                unitId: supportRequest.unitId,
                priority: supportRequest.priority.toUpperCase(),
                propertyId
              }

              logger.logInfo('Creating support ticket', {
                ticket
              })

              const mutationPromise = createTicket(ticket).unwrap()

              mutationPromise
                .then(() => navigate(paths.SupportRequests))
                .catch((e: any) => {
                  logger.logError('Failed to create support ticket.', {
                    error: e
                  })
                })

              toast.promise(mutationPromise, {
                loading: 'Support ticket submitting...',
                success: 'Support ticket created',
                error: 'Support ticket creation failed'
              })
            }}
            text="Submit Request"
            loadText="Submitting..."
            disabled={!isValid(supportRequest)}
            loading={createTicketResult.isLoading}
          />
        </div>
      </div>
    </section>
  )
}

export default AddSupportRequestPage
